import React from "react";
import styled from "styled-components";
import Title from "../atoms/typography/Title";
import { HorizontalSpacer } from "../atoms/Spacers";
import TabBar from "../molecules/TabBar";
import ClientCardHeader from "../molecules/ClientCardHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { userManager } from "../../auth/oidcConfig";
import ClientAml from "./aml/ClientAml";
import UnauthorizedPage from "../molecules/UnauthorizedPage";
import { usePermissions } from "../../graphql/hooks/usePermissions";
import LoadingSpinner from "../atoms/LoadingSpinner";
import {
  AgreementsPermissions,
  BlacklistPermissions,
  MonitoringPermissions,
  OnboardingPermissions,
  RuleBucketsPermissions,
  SettingsPermissions,
  UsersPermissions,
  type AvailableReadPermission,
} from "../../utils/permissions";
import ClientInvoicesPage from "./client/ClientInvoicesPages";
import Alert from "../molecules/Alert";
import ClientOverviewBeta from "./client/ClientOverviewBeta";
import ClientFilesPage from "./client/ClientFilesPage";

const FrameWrapper = styled.div`
  width: 80vw;
  height: calc(100vh - 100px);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  padding: 1rem;
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

interface OldSntWireframedTemplateProps {
  path: string;
}

const OldSntWireframedTemplate: React.FC<OldSntWireframedTemplateProps> = ({
  path,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, objectsAsBase64 } = useParams();
  const queryString = "?snt2=true";
  const host = import.meta.env.VITE_SNT1_HOST;
  const currentHost = import.meta.env.VITE_HOST;

  const [selectedTab, setSelectedTab] = React.useState<string>("overview");
  const [src, setSrc] = React.useState<string | null>(null);
  const [clientIdentifier, setClientIdentifier] = React.useState<string | null>(
    null,
  );

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const { permissions, error, loading } = usePermissions();
  const userPermissions = permissions?.availableForCurrentUser.readPermissions;

  if (error) {
    setErrorMessage(error.message);
  }

  const checkIfUserIsAuthorized = (
    pagePermissions: Array<AvailableReadPermission>,
  ) => {
    if (pagePermissions.length === 0) {
      return true;
    } else {
      return pagePermissions.some((permission) =>
        userPermissions?.includes(permission),
      );
    }
  };

  React.useEffect(() => {
    const getUserToken = async () => {
      const user = await userManager.getUser();
      if (user && user.access_token) {
        // Same behavior as old Vue version
        const base64Data = btoa(JSON.stringify({ token: user.access_token }));
        sessionStorage.setItem("snt-shared-data", base64Data);
      }
    };

    getUserToken();
  }, []);

  React.useEffect(() => {
    if (loading) return; // wait until permissions loaded
    if (!permissions) return;

    if (path === "person") {
      setSrc(`${host}/person${queryString}`);
    } else if (path === "invoices-edit") {
      setSrc(`${host}/invoices-edit${queryString}`);
    } else if (path === "invoices") {
      setSrc(`${host}/invoices${queryString}`);
    } else if (path === "tolkien") {
      setSrc(`${host}/tolkien${queryString}`);
    } else if (path === "rule-buckets") {
      if (!checkIfUserIsAuthorized(RuleBucketsPermissions)) {
        setSrc("unauthorized");
        return;
      } else {
        setSrc(`${host}/rule-buckets${queryString}`);
      }
    } else if (path === "rating") {
      setSrc(`${host}/rating${queryString}`);
    } else if (path === "prospect") {
      setSrc(`${host}/prospect${queryString}`);
    } else if (path === "onboardings") {
      if (!checkIfUserIsAuthorized(OnboardingPermissions)) {
        setSrc("unauthorized");
        return;
      } else {
        setSrc(`${host}/onboardings${queryString}`);
      }
    } else if (path === "agreements") {
      if (!checkIfUserIsAuthorized(AgreementsPermissions)) {
        setSrc("unauthorized");
        return;
      } else {
        setSrc(`${host}/agreements${queryString}`);
      }
    } else if (path === "monitoring-triggers") {
      if (!checkIfUserIsAuthorized(MonitoringPermissions)) {
        setSrc("unauthorized");
        return;
      } else {
        setSrc(`${host}/monitoring-triggers${queryString}`);
      }
    } else if (path === "blacklist") {
      if (!checkIfUserIsAuthorized(BlacklistPermissions)) {
        setSrc("unauthorized");
        return;
      } else {
        setSrc(`${host}/blacklist${queryString}`);
      }
    } else if (path === "users") {
      if (!checkIfUserIsAuthorized(UsersPermissions)) {
        setSrc("unauthorized");
        return;
      } else {
        setSrc(`${host}/users${queryString}`);
      }
    } else if (path === "kyc") {
      setSrc(`${host}/kyc${queryString}`);
    } else if (path === "batchdetermination") {
      setSrc(`${host}/batchdetermination${queryString}`);
    } else if (path === "settings") {
      if (!checkIfUserIsAuthorized(SettingsPermissions)) {
        setSrc("unauthorized");
        return;
      } else {
        setSrc(`${host}/settings${queryString}`);
      }
    } else if (path === "interpretation-result") {
      setSrc(
        `${currentHost}/snt2/company/${id}/template/interpretation-result/${objectsAsBase64}${queryString}`,
      );
    } else if (path === "clientTemplates") {
      setClientIdentifier(id ?? "");
      setSrc(`${host}/c/${id}/30${queryString}`);
    } else if (path === "clientAml") {
      setClientIdentifier(id ?? "");
      setSelectedTab("aml");
      setSrc("aml");
    } else if (path === "invoicesNew") {
      setClientIdentifier(id ?? "");
      setSelectedTab("invoicesNew");
      setSrc("invoicesNew");
    } else if (path === "documentAgreementsSecurities") {
      setClientIdentifier(id ?? "");
      setSelectedTab("documentAgreementsSecurities");
      setSrc("documentAgreementsSecurities");
    } else {
      setClientIdentifier(id ?? "");
      if (path === "client") {
        setSrc(`${host}/c/${id}/${queryString}`);
      } else if (path.startsWith("/client/document")) {
        setSrc(`${host}/c/${id}/2${queryString}`);
      } else if (path.startsWith("/client")) {
        setSrc(`${host}/c/${id}/${queryString}`);
      } else if (path.startsWith("/credit-information")) {
        setSrc(`${host}/creditinfo${queryString}`);
      }
    }
  }, [path, permissions, usePermissions]);

  if (!src) {
    return (
      <FrameWrapper>
        <p>Ingen wireframe tillgänglig för denna sida.</p>
      </FrameWrapper>
    );
  }

  const tabs = [
    {
      id: "overview",
      name: "clientCard.overview",
      url: `${host}/c/${id}/${queryString}`,
    },
    {
      id: "externalInformation",
      name: "clientCard.externalInformation",
      url: `${host}/c/${id}/1${queryString}`,
    },
    {
      id: "documents",
      name: "clientCard.documents",
      url: `${host}/c/${id}/2${queryString}`,
    },
    {
      id: "limits",
      name: "clientCard.limits",
      url: `${host}/c/${id}/21${queryString}`,
    },
    {
      id: "exceptions",
      name: "clientCard.exceptions",
      url: `${host}/c/${id}/6${queryString}`,
    },
    {
      id: "tasks",
      name: "clientCard.tasks",
      url: `${host}/c/${id}/7${queryString}`,
    },
    {
      id: "invoices",
      name: "clientCard.invoices",
      url: `${host}/c/${id}/8${queryString}`,
    },
    {
      id: "organisation",
      name: "clientCard.boardOrganisation",
      url: `${host}/c/${id}/9${queryString}`,
    },
    {
      id: "invitedPeople",
      name: "clientCard.invitedPeople",
      url: `${host}/c/${id}/10${queryString}`,
    },
    {
      id: "recipients",
      name: "clientCard.recipientList",
      url: `${host}/c/${id}/11${queryString}`,
    },
    /*{
      id: "templates",
      name: "clientCard.templates",
      url: `templates`,
    },*/
    {
      id: "templatesFrame",
      name: "Mallar (gamla)",
      url: `${host}/c/${id}/30${queryString}`,
    },
    {
      id: "aml",
      name: "clientCard.aml",
      url: `aml`,
    },
    {
      id: "settings",
      name: "clientCard.settings",
      url: `${host}/c/${id}/99${queryString}`,
    },
    {
      id: "invoicesNew",
      name: "clientCard.invoices2",
      url: `invoices`,
    },
    {
      id: "documentAgreementsSecurities",
      name: "clientCard.documentAgreementsSecurities",
      url: `files`,
    },
  ];

  const onClickTab = (tabId: string) => {
    setSelectedTab(tabId);
    const tab = tabs.find((t) => t.id === tabId);
    if (tabId === "aml") {
      setSrc("aml");
      navigate(`/client/${id}/aml`);
      return;
    }
    if (tabId === "invoicesNew") {
      setSrc("invoicesNew");
      navigate(`/client/${id}/invoicesNew/documents`);
      return;
    }
    if (tabId === "documentAgreementsSecurities") {
      setSrc("documentAgreementsSecurities");
      navigate(`/client/${id}/files`);
      return;
    }
    /*if (tabId === "templates") {
      setSrc("templates");
      navigate(`/client/${id}/templates`);
      return;
    }*/
    if (tab) {
      setSrc(tab.url);
    }
  };

  return (
    <div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        src === "unauthorized" && <UnauthorizedPage />
      )}
      {(path.includes("client") ||
        path === "invoicesNew" ||
        path === "documentAgreementsSecurities") && (
        <div>
          <Title>{t("clientCard.clientCard")}</Title>
          <HorizontalSpacer spacing={2} />
          <TabBar
            tabs={tabs}
            onSelectTab={onClickTab}
            selectedTabId={selectedTab}
          />
          <HorizontalSpacer spacing={1} />
          <ClientCardHeader
            clientIdentifier={clientIdentifier || "5561234567"}
          />
        </div>
      )}
      {src === "aml" && clientIdentifier && (
        <ClientAml clientIdentifier={clientIdentifier} />
      )}
      {src === "invoicesNew" && clientIdentifier && (
        <ClientInvoicesPage clientIdentifier={clientIdentifier} />
      )}
      {src === "documentAgreementsSecurities" && clientIdentifier && (
        <ClientFilesPage clientIdentifier={clientIdentifier} />
      )}
      {src !== "aml" &&
        src !== "unauthorized" &&
        src !== "invoicesNew" &&
        src !== "documentAgreementsSecurities" && (
          <FrameWrapper>
            <Iframe src={src} title={`Wireframe for ${path}`} />
          </FrameWrapper>
        )}
      {errorMessage && (
        <Alert
          type="error"
          title={errorMessage?.toString() ?? t("errors.somethingWentWrong")}
        />
      )}
      {selectedTab === "overview" && path === "client" && clientIdentifier && (
        <ClientOverviewBeta clientIdentifier={clientIdentifier} />
      )}
    </div>
  );
};

export default OldSntWireframedTemplate;
